import { playButtonClickSound } from '@wg/web2clientapi/sound';
import equal from 'fast-deep-equal/react';
import isFunction from 'lodash/isFunction';
import React, {
    MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef, useState
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Mousewheel } from 'swiper/modules';
import classnames from 'classnames';

import styles from './ProgressBar.scss';
import { ProgressBarController } from './ProgressBarController';
import ProgressBarMask from './ProgressBarMask';
import { ScrollState, updateButtonsAndGradient } from './utils';
import {
    MASK_TAIL_WIDTH,
    MOUSE_LEAVE_DELAY,
    PROGRESSBAR_LEAVE_TIMER_ID,
    SLIDE_WIDTH,
    SQUIRCLE_WIDTH,
    SVG_PROGRESS_ITEMS_MASK_ID
} from './constants';

import type { Swiper as SwiperInstance } from 'swiper/types';
import type Api from '~/types';
import type { RootState } from '~/store';

import { useAppSelector } from '~/hooks';
import ClientApi from '~/clientApi';
import { premiumBoosterProgress, secondaryShip } from '~/selectors';
import timer from '~/utils/timer';
import { getShipIdFromReward } from '~/utils/rewards';
import ProgressBarItem from '~/Components/ProgressBatItem/ProgressBarItem';
import FinalReward from '~/Components/ProgressBar/FinalReward/FinalReward';
import PostProgression from '~/Components/ProgressBar/PostProgression/PostProgression';

export interface ProgressBar_Props {
    [propName: string]: unknown,
}

export type ProgressBarRefFunc = (ref: ProgressBarController) => void;

export type ProgressBarRef = ProgressBarRefFunc | MutableRefObject<ProgressBarController>;

export interface ProgressBarState {
    rewards: Array<Api.IReward>,
    animationProgress: number,
    premiumBoosterProgress: number,
    shipLevels: Array<Api.ShipLevel>,
    issuedRewards: Array<Api.IReward>,
    isAnimationInProgress: boolean,
    blueprintsCount: number,
    isBlueprintsViewerVisible: boolean,
    accountProgress: number,
    freeLevels: number,
    shipPreviewBackground: string | null,
    secondaryShip: Api.GameItemReward | null,
    currency: string,
}

export const stateSelector = (state: RootState): ProgressBarState => {
    return {
        rewards: state.app.rewards,
        animationProgress: state.app.animationShipLevel,
        premiumBoosterProgress: premiumBoosterProgress(state),
        shipLevels: state.app.shipLevels,
        issuedRewards: state.app.account.issuedRewards,
        isAnimationInProgress: state.app.isAnimationInProgress,
        blueprintsCount: state.app.blueprintsCount,
        isBlueprintsViewerVisible: state.dialogs.isBlueprintsViewerVisible,
        accountProgress: state.app.account.progress as number,
        freeLevels: state.app.freeLevels,
        shipPreviewBackground: state.app.shipPreviewBackground,
        secondaryShip: secondaryShip(state),
        currency: state.app.currency,
    };
};

const ProgressBar = React.forwardRef((props: ProgressBar_Props, ref: ProgressBarRef) => {
    const {
        rewards,
        animationProgress,
        premiumBoosterProgress,
        shipLevels,
        issuedRewards,
        isAnimationInProgress,
        blueprintsCount,
        isBlueprintsViewerVisible,
        accountProgress,
        freeLevels,
        shipPreviewBackground,
        secondaryShip,
        currency,
    } = useAppSelector(stateSelector, equal);

    const swiperRef = useRef<SwiperInstance | null>(null);
    const swiperWrapper = useRef<HTMLDivElement>();
    const swiperNextButton = useRef<HTMLDivElement>();
    const swiperPrevButton = useRef<HTMLDivElement>();
    const scrollState = useRef<ScrollState>('start');
    const mouseIsOver = useRef<boolean>(false);
    const [localProgress, setLocalProgress] = useState<number | null>(null);
    const [isScrolling, setIsScrolling] = useState<boolean>(false);
    const [scrollingKeySuffix, setScrollingKeySuffix] = useState<string>('');
    const reachableCount = rewards.length > 0 ? freeLevels + premiumBoosterProgress : 0;

    let isFreeMainReward = false;
    if (freeLevels && currency) {
        const freeLevelsWithCompensation = freeLevels + (secondaryShip?.compensation?.[currency] || 0);
        if (freeLevelsWithCompensation >= rewards.length) {
            isFreeMainReward = true;
        }
    }

    const fadedIndexes = rewards.filter((reward) => !!getShipIdFromReward(reward)).map((r) => r.shipLevelNumber);
    const getIndex = (indexParam: number): number => {
        const width = swiperWrapper.current?.clientWidth || 0;
        const indexOffset = Math.ceil(width / SLIDE_WIDTH / 2);
        const index = indexParam - indexOffset;
        return index;
    };

    const goNext = useCallback(() => {
        playButtonClickSound();
        if (swiperRef.current !== null) {
            swiperRef.current.slideTo(swiperRef.current.activeIndex + 3);
        }
    }, []);

    const goToEnd = useCallback(() => {
        playButtonClickSound();
        if (swiperRef.current !== null && swiperRef.current.slideTo) {
            swiperRef.current.slideTo(rewards.length);
        }
    }, [rewards]);

    const goPrev = useCallback(() => {
        playButtonClickSound();
        if (swiperRef.current !== null) {
            swiperRef.current.slideTo(swiperRef.current.activeIndex - 3);
        }
    }, []);

    const goTo = useCallback((index: number) => {
        if (swiperRef.current !== null && swiperRef.current.slideTo) {
            swiperRef.current.slideTo(index);
        }
    }, []);

    const checkScrollPosition = useCallback((force = false) => {
        if (!timer.isRunning(PROGRESSBAR_LEAVE_TIMER_ID) || force) {
            setTimeout(() => {
                if (swiperRef.current && swiperWrapper.current && !mouseIsOver.current) {
                    const index = getIndex(animationProgress);
                    goTo(index);
                }
            }, 10);
        }
    }, [animationProgress, goTo]);

    useEffect(() => {
        goTo(getIndex(accountProgress));
    }, [goTo, accountProgress]);

    useLayoutEffect(() => {
        const width = swiperWrapper.current ? swiperWrapper.current.clientWidth : 0;
        const controller = new ProgressBarController({
            mouseIsOver,
            checkScrollPosition,
            setLocalProgress,
            goTo,
            width,
        });

        if (ref) {
            if (isFunction(ref)) {
                ref(controller);
            } else {
                ref.current = controller;
            }
        }
    }, [checkScrollPosition, goTo, ref]);

    const setSwiperRef = useCallback((ref?: SwiperInstance) => {
        if (ref) {
            swiperRef.current = ref;
        }
    }, []);

    const setSwiperWrapper = useCallback((ref?: HTMLDivElement | null) => {
        if (ref) {
            swiperWrapper.current = ref;
        }
    }, []);

    const setSwiperNextButton = useCallback((ref?: HTMLDivElement | null) => {
        if (ref) {
            swiperNextButton.current = ref;
        }
    }, []);

    const setSwiperPrevButton = useCallback((ref?: HTMLDivElement | null) => {
        if (ref) {
            swiperPrevButton.current = ref;
        }
    }, []);

    const onMouseOver = useCallback(() => {
        if (mouseIsOver.current) {
            return;
        }
        mouseIsOver.current = true;
        ClientApi.setScrollover(true);
        timer.clear(PROGRESSBAR_LEAVE_TIMER_ID);
    }, []);

    const onMouseLeave = useCallback(() => {
        mouseIsOver.current = false;
        if (!isBlueprintsViewerVisible) {
            ClientApi.setScrollover(false);
        }
        timer.start(PROGRESSBAR_LEAVE_TIMER_ID, MOUSE_LEAVE_DELAY, () => {
            checkScrollPosition();
        });
    }, [checkScrollPosition, isBlueprintsViewerVisible]);

    const onParentWheelPrevent = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
        e.stopPropagation();
    }, []);

    const onInit = useCallback(() => {
        setIsScrolling(false);
    }, []);

    const tooltipsTimeout = useRef<NodeJS.Timeout | undefined>();
    const delayedUpdateTooltips = useCallback(() => {
        clearTimeout(tooltipsTimeout.current);
        tooltipsTimeout.current = setTimeout(() => {
            setIsScrolling(false);
            setScrollingKeySuffix(`updated_${Date.now()})`);
        }, 100);
    }, []);

    const onProgress = useCallback(() => {
        if (swiperRef.current) {
            updateButtonsAndGradient(
                swiperRef.current,
                scrollState,
                swiperWrapper as MutableRefObject<HTMLDivElement>,
                swiperNextButton as MutableRefObject<HTMLDivElement>,
                swiperPrevButton as MutableRefObject<HTMLDivElement>
            );
        }
    }, []);

    const onSliderScroll = useCallback((_swiper: SwiperInstance, e: WheelEvent) => {
        if (swiperRef.current && typeof e.deltaY === 'number') {
            swiperRef.current.slideTo(swiperRef.current.activeIndex + (e.deltaY > 0 ? 1 : -1));
        }
    }, []);

    const onTouchMove = useCallback(() => {
        window.tooltipProvider?.hide?.();
    }, []);

    const onTransitionStart = useCallback(() => {
        window.tooltipProvider?.hide?.();
        setIsScrolling(true);
    }, []);

    const onTransitionEnd = useCallback(() => {
        delayedUpdateTooltips();
    }, [delayedUpdateTooltips]);

    const getBarPercentOffset = (progress: number, maxProgress?: number) => {
        let p = progress;
        if (maxProgress !== undefined && p > maxProgress) {
            p = maxProgress;
        }
        const offset = (rewards.length - p) * SLIDE_WIDTH + MASK_TAIL_WIDTH - SQUIRCLE_WIDTH;
        return -offset;
    };

    const getGradient = (): string => {
        const fullWidth = SLIDE_WIDTH * rewards.length;
        let breakpoints = '';
        const half = SLIDE_WIDTH / 2;
        const offset = 130;
        fadedIndexes.forEach((index) => {
            const center = SLIDE_WIDTH * index - half;
            breakpoints += `black ${center - offset}px, `;
            breakpoints += `transparent ${center - offset + 30}px, `;
            breakpoints += `transparent ${center + offset - 30}px, `;
            breakpoints += `black ${center + offset}px, `;
        });
        const gradient = `linear-gradient(to right, black 0px, ${breakpoints} black ${fullWidth}px)`;
        return gradient;
    };

    useEffect(() => {
        onProgress();
        checkScrollPosition();
    }, [checkScrollPosition, animationProgress, onProgress]);

    const renderBar = () => {
        const fullWidth = SLIDE_WIDTH * rewards.length;
        const barHolderWidth = fullWidth;
        const barProgress = localProgress !== null ? localProgress : animationProgress;
        const barTranslate = getBarPercentOffset(barProgress);
        const barBlueprintTranslate = getBarPercentOffset(barProgress, blueprintsCount);

        return (
            <div
                className={styles.barHolderWrapper}
                style={{
                    width: barHolderWidth,
                    WebkitMaskImage: getGradient(),
                    maskImage: getGradient(),
                }}
            >
                <div
                    className={styles.barHolder}
                    style={{
                        clipPath: `url(#${SVG_PROGRESS_ITEMS_MASK_ID})`,
                        WebkitClipPath: `url(#${SVG_PROGRESS_ITEMS_MASK_ID})`,
                    }}
                >
                    <div
                        id={'progressbar-bar'}
                        className={styles.progress}
                        style={{
                            transform: `translate(${barTranslate}px, 0px)`,
                        }}
                    />
                    <div
                        id={'progressbar-bar-blueprint'}
                        className={styles.progressBlueprint}
                        style={{
                            transform: `translate(${barBlueprintTranslate}px, -70px)`,
                        }}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            <ProgressBarMask rewards={rewards} />
            <div
                className={classnames(styles.wrapper, {
                    [styles.isScrolling]: isScrolling,
                })}
                id={'progressBar'}
                onMouseOver={onMouseOver}
                onMouseDown={onMouseOver}
                onMouseLeave={onMouseLeave}
            >
                <div
                    ref={setSwiperWrapper}
                    onWheel={onParentWheelPrevent}
                >
                    <Swiper
                        modules={[FreeMode, Mousewheel]}
                        slidesPerView={'auto'}
                        freeMode={true}
                        mousewheel={{
                            sensitivity: 0,
                        }}
                        key={`Rewards${rewards.length}`}
                        passiveListeners={true}
                        onInit={onInit}
                        onSwiper={setSwiperRef}
                        onProgress={onProgress}
                        onScroll={onSliderScroll}
                        onTouchMove={onTouchMove}
                        onTransitionStart={onTransitionStart}
                        onTransitionEnd={onTransitionEnd}
                    >
                        {rewards.map((reward: Api.IReward, i: number) => {
                            const isLast = i === rewards.length - 1;
                            if (isLast) {
                                return null;
                            }
                            const isFirst = i === 0;
                            const isActive = animationProgress === i + 1 && !isAnimationInProgress;
                            const isPremiumLevel = reward.shipLevelNumber > reachableCount;
                            const shipLevel = shipLevels[i + 1];
                            const isReached = Number(shipLevel.level) <= animationProgress;
                            const isBlueprint = i < blueprintsCount;

                            const issuedReward = issuedRewards.filter((issuedReward) => {
                                return issuedReward.id === reward.id;
                            })[0];

                            return (
                                <SwiperSlide
                                    id={`ProgressBarItem-${i}`}
                                    key={`r${i}`}
                                    style={{ width: SLIDE_WIDTH }}
                                    className={styles.progressBarItemWrapper}
                                >
                                    {isFirst && renderBar()}
                                    <ProgressBarItem
                                        keySuffix={scrollingKeySuffix}
                                        issuedReward={issuedReward}
                                        shipLevel={shipLevel}
                                        reward={reward}
                                        isPremiumLevel={isPremiumLevel}
                                        isReached={isReached}
                                        index={i}
                                        isActive={isActive}
                                        isBlueprint={isBlueprint}
                                        isFreeMainReward={isFreeMainReward}
                                        secondaryShip={secondaryShip}
                                    />
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
                <div className={styles.nextButton} onClick={goNext} ref={setSwiperNextButton} />
                <div className={styles.prevButton} onClick={goPrev} ref={setSwiperPrevButton} />
            </div>
            <div className={styles.finalReward}>
                <FinalReward
                    level={rewards.length}
                    rewards={rewards}
                    reachableCount={reachableCount}
                    isCollected={!!issuedRewards.filter((issuedReward) => issuedReward.isMainReward)[0]}
                    isShipPreviewAvailable={!!shipPreviewBackground}
                    secondaryShip={secondaryShip}
                    isFreeMainReward={isFreeMainReward}
                    onClick={goToEnd}
                />
                <PostProgression />
            </div>
        </>
    );
});

export default ProgressBar;
